// boxShadow on web, shadow* props on native
//
// Plaud-minimal collapses elevation to two soft recipes (mockup
// --shadow-soft / --shadow-float): `soft` for gently lifted chrome (search
// pill, in-page popovers) and `float` for genuinely floating surfaces
// (sheets, dropdowns, dialogs, DockedPanel). In-page separation is hairline
// or surface tone, never shadow. `marker` survives untouched — it is tuned
// for separation from map tiles, not page chrome; do not reuse it in-page.
// The *Glow keys are aliases of `soft` kept only so the readiness hero
// compiles until its phase-2 re-skin; don't use them in new code.
import { Platform } from 'react-native';

const nativeSoft = {
  shadowColor: '#141414',
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.08,
  shadowRadius: 10,
  elevation: 2,
} as const;

const nativeFloat = {
  shadowColor: '#141414',
  shadowOffset: { width: 0, height: 8 },
  shadowOpacity: 0.12,
  shadowRadius: 18,
  elevation: 8,
} as const;

const nativeShadows = {
  soft: nativeSoft,
  float: nativeFloat,
  // Legacy tier aliases — migrate call sites to soft/float as screens are
  // re-skinned, then delete.
  sm: nativeSoft,
  md: nativeSoft,
  paper: nativeSoft,
  lg: nativeFloat,
  xl: nativeFloat,
  floating: nativeFloat,
  primaryGlow: nativeSoft,
  successGlow: nativeSoft,
  dangerGlow: nativeSoft,
  warningGlow: nativeSoft,
  neutralGlow: nativeSoft,
  // Map markers (cluster bubbles, observation dots) — tight, strong shadow
  // tuned for separation from map tiles, not page chrome.
  marker: {
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 4,
    elevation: 4,
  },
} as const;

const webSoft = {
  boxShadow: '0px 1px 2px rgba(20, 20, 20, 0.05), 0px 10px 28px -18px rgba(20, 20, 20, 0.16)',
} as const;

const webFloat = {
  boxShadow: '0px 2px 4px rgba(20, 20, 20, 0.05), 0px 18px 44px -18px rgba(20, 20, 20, 0.2)',
} as const;

const webShadows = {
  soft: webSoft,
  float: webFloat,
  sm: webSoft,
  md: webSoft,
  paper: webSoft,
  lg: webFloat,
  xl: webFloat,
  floating: webFloat,
  primaryGlow: webSoft,
  successGlow: webSoft,
  dangerGlow: webSoft,
  warningGlow: webSoft,
  neutralGlow: webSoft,
  marker: { boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.35)' },
} as const;

export const ELEVATION = Platform.OS === 'web' ? webShadows : nativeShadows;

export type ElevationKey = keyof typeof nativeShadows;
